import { useState } from 'react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { CheckCircleIcon, XCircleIcon } from '@/components/icons';
import { getApiErrorMessage } from '@/api/errors';
import type { TestPleskRequest, TestPleskResponse } from '@/types/node';

export interface PleskCredentials {
  host: string;
  login: string;
  password: string;
}

export interface PleskSettingsSectionProps {
  values: PleskCredentials;
  onChange: (field: keyof PleskCredentials, value: string) => void;
  errors: Partial<Record<keyof PleskCredentials, string>>;
  /** Show the DNS management toggle */
  supportsDns: boolean;
  dnsEnabled: boolean;
  onDnsEnabledChange: (enabled: boolean) => void;
  isEditMode: boolean;
  disabled?: boolean;
  onTestConnection: (data: TestPleskRequest) => Promise<TestPleskResponse>;
}

/**
 * Plesk panel credentials with a "Test Connection" check
 */
export function PleskSettingsSection({
  values,
  onChange,
  errors,
  supportsDns,
  dnsEnabled,
  onDnsEnabledChange,
  isEditMode,
  disabled = false,
  onTestConnection,
}: PleskSettingsSectionProps) {
  const [isTesting, setIsTesting] = useState(false);
  const [testResult, setTestResult] = useState<TestPleskResponse | null>(null);

  const handleChange = (field: keyof PleskCredentials, value: string) => {
    onChange(field, value);
    setTestResult(null);
  };

  const canTest = !!(values.host.trim() && values.login.trim() && values.password.trim());

  const handleTest = async () => {
    if (!canTest) return;
    setIsTesting(true);
    setTestResult(null);
    try {
      setTestResult(
        await onTestConnection({
          plesk_host: values.host.trim(),
          plesk_login: values.login.trim(),
          plesk_password: values.password.trim(),
        })
      );
    } catch (error) {
      setTestResult({ success: false, error: getApiErrorMessage(error, 'Connection failed') });
    } finally {
      setIsTesting(false);
    }
  };

  return (
    <div className="node-modal-plesk-section">
      <div className="node-modal-section-title">Plesk Settings</div>
      <Input
        label="Plesk Panel URL"
        placeholder="https://plesk.example.com:8443"
        value={values.host}
        onChange={(e) => handleChange('host', e.target.value)}
        error={errors.host}
        required
        disabled={disabled}
        helperText="Full URL to Plesk panel (must start with https://)"
      />
      <Input
        label="Admin Login"
        placeholder="admin"
        value={values.login}
        onChange={(e) => handleChange('login', e.target.value)}
        error={errors.login}
        required={!isEditMode}
        disabled={disabled}
        helperText="Plesk administrator username"
      />
      <Input
        label="Admin Password"
        type="password"
        placeholder={isEditMode ? '(unchanged)' : 'Enter admin password'}
        value={values.password}
        onChange={(e) => handleChange('password', e.target.value)}
        error={errors.password}
        required={!isEditMode}
        disabled={disabled}
        autoComplete="new-password"
        helperText={isEditMode ? 'Leave empty to keep existing password' : 'Plesk administrator password'}
      />
      <div className="node-modal-test-connection">
        <Button
          type="button"
          variant="secondary"
          size="sm"
          onClick={handleTest}
          isLoading={isTesting}
          disabled={isTesting || disabled || !canTest}
        >
          Test Connection
        </Button>
        {testResult && (
          <div className={`node-modal-test-result ${testResult.success ? 'success' : 'error'}`} role="status">
            {testResult.success ? (
              <div className="node-modal-test-success">
                <CheckCircleIcon size={null} className="node-modal-test-icon" />
                <span>
                  Connected: {testResult.hostname} (Plesk {testResult.panel_version},{' '}
                  {testResult.domains_count} domains)
                </span>
              </div>
            ) : (
              <div className="node-modal-test-error">
                <XCircleIcon size={null} className="node-modal-test-icon" />
                <span>Connection failed: {testResult.error}</span>
              </div>
            )}
          </div>
        )}
      </div>
      {supportsDns && (
        <label className="node-modal-checkbox">
          <input
            type="checkbox"
            checked={dnsEnabled}
            onChange={(e) => onDnsEnabledChange(e.target.checked)}
            disabled={disabled}
          />
          <span className="node-modal-checkbox-label">
            <strong>Enable DNS Management</strong>
            <span className="node-modal-checkbox-description">
              Allow managing DNS records via Plesk API
            </span>
          </span>
        </label>
      )}
    </div>
  );
}
